import * as RadixDialog from '@radix-ui/react-dialog';
import {
  startTransition,
  useCallback,
  useLayoutEffect,
  useRef,
  ViewTransition,
  type ReactNode,
} from 'react';
import { useBackToClose } from '../lib/back-to-close';

/**
 * Kozos dialogus-keret.
 *
 * Radix ala: fokuszcsapda, Escape, aria-attributumok es a hatter inertte tetele
 * mind onmuködoen jon. Ezeket kezzel ujrairni sok apro hiba forrasa lenne.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  width = 'md',
  padded = true,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  width?: 'md' | 'lg';
  /** A parancspalettanal a tartalom maga adja a belso terkozt. */
  padded?: boolean;
}) {
  const returnFocus = useReturnFocus(open);
  // Telefonon a vissza-mozdulat a parbeszedet zarja, nem a csatornat (K2).
  useBackToClose(open, () => onOpenChange(false));
  return (
    <RadixDialog.Root
      open={open}
      // Escape es hatterkattintas: a bezaras atmenetkent fut (lasd lent).
      onOpenChange={(next) => startTransition(() => onOpenChange(next))}
    >
      {/*
        A BEZARAS lathato: a parbeszed visszahuzodik, nem egy pillanat alatt
        tunik el - latszik, hova ter vissza a figyelem. A megnyitast a sajat
        CSS-animacio (fade-rise) viszi.

        Ehhez a tartalmat MI csatoljuk le (`open &&` + forceMount), nem a Radix:
        a <ViewTransition> kilepo animacioja csak akkor sul el, ha maga is
        kikerul a fabol - a Radix sajat eltuntetese alatt a helyen maradna.
      */}
      {open && (
        // A <ViewTransition> a Portalon KIVUL all: a Portal minden kozvetlen
        // gyerekere ref-et tesz, es egy ViewTransition-re tett ref nem DOM-
        // elem - a Radix getComputedStyle-ja elszallna tole, es vele a lap.
        <ViewTransition exit="panel-close" default="none">
          <RadixDialog.Portal forceMount>
            <RadixDialog.Overlay
              forceMount
              className="animate-fade-in fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]"
            />
            {/*
          A parbeszed OSZLOP: rogzitett fejlec, gorgetheto torzs.
          Korabban az egesz doboznak volt egy `overflow-hidden`-je, es a hosszu
          tartalom alja - a mento gombokkal egyutt - egyszeruen levagodott.
          Igy a gorgetes ott van, ahol a tartalom, es a fejlec a helyen marad.
        */}
            <RadixDialog.Content
              forceMount
              onCloseAutoFocus={returnFocus}
              className={[
                'animate-fade-rise fixed left-1/2 z-50 flex w-[calc(100vw-2rem)] -translate-x-1/2 flex-col',
                // A LATHATO terulethez merve (lib/viewport.ts): nyitott billentyuzetnel
                // a gombok nem csuszhatnak a billentyuzet ala.
                'top-[calc(var(--app-height,100dvh)*0.06)] max-h-[calc(var(--app-height,100dvh)*0.88)] rounded-card border border-border-soft',
                'bg-surface-raised text-ink shadow-(--shadow-modal)',
                width === 'lg' ? 'max-w-2xl' : 'max-w-md',
                /*
                  TELEFONON ALULROL JOVO LAP (K2): a hüvelykujj alatt van, es
                  ugy nez ki, mint a telefon sajat lapjai. Az also szele a
                  billentyuzet tetejehez igazodik (a layout es a lathato terulet
                  kulonbsege), alul a biztonsagi sav ad helyet a gomboknak.
                */
                'max-sm:inset-x-0 max-sm:top-auto max-sm:bottom-[calc(100dvh-var(--app-height,100dvh))] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0',
                'max-sm:rounded-b-none max-sm:border-x-0 max-sm:border-b-0 max-sm:pb-[env(safe-area-inset-bottom)]',
              ].join(' ')}
              // Leiras nelkul a Radix FIGYELMEZTET, ha nincs explicit undefined.
              {...(description ? {} : { 'aria-describedby': undefined })}
            >
              {/* A cim MINDIG kirajzolodik: a Radix a11y-hoz megkoveteli. */}
              <div className={padded ? 'shrink-0 px-6 pt-6' : 'sr-only'}>
                <RadixDialog.Title
                  className={padded ? 'font-display text-lg font-semibold' : 'sr-only'}
                >
                  {title}
                </RadixDialog.Title>

                {description && (
                  <RadixDialog.Description
                    className={padded ? 'mt-1 text-sm text-ink-muted' : 'sr-only'}
                  >
                    {description}
                  </RadixDialog.Description>
                )}
              </div>

              <div
                className={
                  padded
                    ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-6'
                    : 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-card'
                }
              >
                {children}
              </div>
            </RadixDialog.Content>
          </RadixDialog.Portal>
        </ViewTransition>
      )}
    </RadixDialog.Root>
  );
}

export const dialogClose = RadixDialog.Close;

/**
 * A parbeszed SAJAT gombjainak (Megse, Mentes...) bezarasa atmenetkent.
 *
 * Az Escape es a hatterkattintas mar atmenet (a Dialog intezi); a gombok
 * viszont kozvetlenul a szulo `onClose`-at hivjak. A komponens tetejen egyszer
 * becsomagolva minden bezaras ugyanugy visszahuzodik - a hivasi helyek maradnak.
 */
export function useAnimatedClose(close: () => void): () => void {
  const latest = useRef(close);
  latest.current = close;
  return useCallback(() => startTransition(() => latest.current()), []);
}

/**
 * Bezaraskor a fokusz VISSZATER oda, ahonnan az ablakot megnyitottak.
 *
 * A Radix ezt csak a sajat `Dialog.Trigger`-evel tudja; a mi ablakainkat viszont
 * sajat gombok nyitjak allapottal, igy a Radix a bezaraskor semmire nem
 * fokuszalt, es a fokusz a lap elejere (body) esett. Billentyuzettel ez azt
 * jelentette, hogy minden bezaras utan elolrol kellett tabolni.
 *
 * A nyito elemet MEG a Radix fokuszmozgatasa ELOTT jegyezzuk meg (layout effekt
 * - a Radix a sajatjat passziv effektben teszi). Visszaadni csak akkor adjuk,
 * ha kozben mas nem vette at a fokuszt: pl. egy menu bezarasa utan nyilo
 * ujabb ablak a sajatjat mar megkapta.
 */
export function useReturnFocus(open: boolean): (event: Event) => void {
  const opener = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || active === document.body) {
      opener.current = null;
      return;
    }
    /*
      MENUBOL nyitva (pl. "Küldés később → Egyedi időpont…") a menupont a
      menuvel egyutt eltunik - oda nem lehet visszaadni. Ilyenkor a menu
      NYITOGOMBJA a visszateresi pont: a Radix aria-controls-szal koti ossze.
    */
    const menu = active.closest<HTMLElement>('[role="menu"]');
    const menuTrigger = menu?.id
      ? document.querySelector<HTMLElement>(`[aria-controls="${CSS.escape(menu.id)}"]`)
      : null;
    opener.current = menuTrigger ?? active;
  }, [open]);

  return useCallback((event: Event) => {
    // A Radix alapviselkedese itt ugyis nem csinal semmit (nincs Trigger).
    event.preventDefault();
    const target = opener.current;
    const active = document.activeElement;
    const focusFree = !active || active === document.body;
    if (target?.isConnected && focusFree) target.focus();
  }, []);
}
